/**
 * An organization's secrets and its encryption, as a page without the key sees
 * them (§5.25).
 *
 * Two halves, and neither shows a value. The first is **where encryption
 * stands**: switched or not, a rotation owed, and the machines waiting for the
 * key with the safety number each shows — so whoever approves from Airlock can
 * read the number off this page as well as off the waiting machine. This page
 * cannot approve: approving is wrapping the organization key for a machine, and
 * the browser never holds the key.
 *
 * The second is **the organization's own secrets**, by name: who receives each,
 * who set it and when. A value is set in Airlock, where it is sealed before it
 * leaves the machine; this page can only take one away, which needs no key.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import type { PageKeysState } from '@airlock/contracts/cloud/e2e';
import type { OrgSecretList, OrgSecretPresence } from '@airlock/contracts/cloud/org-secrets';
import { color, font } from '../../theme/tokens';
import { Badge, Callout, Empty, Row, RowAction, Table } from '../../components/primitives';
import { useConfirm } from '../../components/Modal';
import { Note, PageBody, PageLede } from '../../components/list';
import { api, apiPath } from '../../lib/api';
import { proseDay } from '../../lib/format';
import type { Organization } from '../../lib/types';
import { actorLabel, failureText } from './shared';

interface TeamList {
  teams: { id: string; name: string }[];
}

export function OrgSecretsPage({ organization, admin }: { organization: Organization | null; admin: boolean }): React.JSX.Element {
  const [keys, setKeys] = useState<PageKeysState | null>(null);
  const [secrets, setSecrets] = useState<OrgSecretList | null>(null);
  const [teams, setTeams] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [dialog, ask] = useConfirm();
  const [removed, setRemoved] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!organization) return;
    setError(null);
    try {
      const [state, list, named] = await Promise.all([
        api.get<PageKeysState>(apiPath`/orgs/${organization.id}/keys`),
        api.get<OrgSecretList>(apiPath`/orgs/${organization.id}/org-secrets`),
        admin ? api.get<TeamList>(apiPath`/orgs/${organization.id}/teams`).catch(() => ({ teams: [] })) : Promise.resolve({ teams: [] })
      ]);
      setKeys(state);
      setSecrets(list);
      setTeams(new Map(named.teams.map((team) => [team.id, team.name])));
    } catch (failure) {
      setError(failureText(failure));
    }
  }, [organization, admin]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!organization) {
    return (
      <PageBody>
        <Empty title="No organization">Secrets belong to an organization.</Empty>
      </PageBody>
    );
  }

  const remove = async (secret: OrgSecretPresence): Promise<void> => {
    const agreed = await ask({
      title: `Remove ${secret.name}?`,
      goes: [`${secret.name}, from every member’s machine at its next check.`],
      stays: [`A workspace’s own ${secret.name}, which already wins over this one.`],
      confirm: 'Remove it',
      danger: true
    });
    if (!agreed) return;
    try {
      setSecrets(await api.del<OrgSecretList>(apiPath`/orgs/${organization.id}/org-secrets/${secret.name}`));
      setRemoved(secret.name);
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  const reach = (secret: OrgSecretPresence): string =>
    secret.reach.teams === null
      ? 'Every member'
      : secret.reach.teams.map((id) => teams.get(id) ?? 'a team').join(', ');

  const waiting = keys?.devices.filter((device) => !device.keyed) ?? [];
  // The status in line (§45.6): the pill and its sentence, then one secondary
  // line that names the machines still waiting, or says none is.
  const named = waiting.map((device) => `${device.user.label}’s ${device.label ?? 'unnamed machine'}`);
  const waitingLine =
    waiting.length === 0
      ? 'No machine is waiting for the key.'
      : `${waiting.length === 1 ? '1 machine is' : `${waiting.length} machines are`} waiting for the key: ${
          named.length > 1 ? `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}` : named[0]
        }. An owner’s or an admin’s Airlock hands it over the next time it opens.`;
  const deviceColumns = 'minmax(0,1.2fr) minmax(0,1fr) 210px 110px';
  const secretColumns = admin ? 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 90px' : 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)';

  return (
    <PageBody max={960}>
      {dialog}
      <PageLede>
        Values every member’s machine is given, sealed on an Airlock machine before they leave it. This page lists them by name
        and never sees one.
      </PageLede>
      {error ? (
        <Callout tone="amber" title="Could not load this page">
          {error}
        </Callout>
      ) : null}

      {keys ? (
        keys.e2eSince ? (
          <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13.5, color: color.inkBody }}>
              <Badge tone="ok">end to end</Badge>
              Since {proseDay(keys.e2eSince)} — only your organization’s machines can read its secrets
            </div>
            <div style={{ fontSize: 13, color: color.inkSubtle, marginTop: 6, lineHeight: 1.55 }}>{waitingLine}</div>
          </div>
        ) : (
          <Callout tone="amber" title="Not end to end yet">
            {keys.legacyValues ? 'This organization’s secret values are still sealed by the account area. ' : 'Secret values would still be sealed by the account area. '}
            Open Airlock on an owner’s or an admin’s machine: it switches the organization at once, and from then on only your machines can
            read them.
          </Callout>
        )
      ) : null}

      {keys?.rotation ? (
        <Callout tone="amber" title="A new key is owed">
          {keys.rotation.reason} The next owner or admin to open Airlock makes a new key and seals every value again. The machine that left
          keeps what it already had — change the credentials themselves if it was lost.
        </Callout>
      ) : null}

      {keys?.e2eSince ? (
        waiting.length ? (
          <Table
            columns={deviceColumns}
            header={['Waiting for the key', 'Member', 'Safety number', 'Since']}
            toolbar={<div style={{ fontSize: 15, fontWeight: 600 }}>Machines waiting</div>}
          >
            {waiting.map((device) => (
              <Row key={device.deviceId} columns={deviceColumns}>
                <span style={{ fontWeight: 500 }}>{device.label ?? 'Unnamed machine'}</span>
                <span style={{ fontSize: 13.5, color: color.inkSubtle }}>{device.user.label}</span>
                <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{device.safetyNumber}</span>
                <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkSubtle }}>{proseDay(device.registeredAt)}</span>
              </Row>
            ))}
          </Table>
        ) : null
      ) : null}
      {keys?.e2eSince && waiting.length ? (
        <Callout title="Approving is done in Airlock">
          {admin
            ? 'An owner or an admin approves any member’s machine, and a member their own other machine, from Airlock → Settings → License. '
            : 'Approve your new machine from your other machine, in Airlock → Settings → License, or ask an owner or an admin. '}
          Compare the safety number there with the one the waiting machine shows: if they differ, do not approve. This browser holds no key, so it
          cannot approve anything.
        </Callout>
      ) : null}

      <div style={{ marginTop: 10 }}>
        {secrets && !secrets.available ? (
          <p style={{ margin: 0, fontSize: 13.5, color: color.inkFaint }}>
            Organization secrets arrive with end-to-end encryption: once this organization has switched, an owner or an admin sets them in Airlock.
          </p>
        ) : secrets && secrets.secrets.length === 0 ? (
          <Empty title="No organization secret yet">
            {admin
              ? 'An owner or an admin sets one in Airlock: Settings › Secrets. It is listed here by name once it is sealed.'
              : 'None reaches you yet.'}
          </Empty>
        ) : secrets ? (
          <Table
            columns={secretColumns}
            header={admin ? ['Name', 'Who receives it', 'Set', ''] : ['Name', 'Who receives it', 'Set']}
            toolbar={<div style={{ fontSize: 15, fontWeight: 600 }}>Organization secrets</div>}
          >
            {secrets.secrets.map((secret) => (
              <Row key={secret.name} columns={secretColumns}>
                <span style={{ fontFamily: font.mono, fontSize: 12.5, wordBreak: 'break-all' }}>{secret.name}</span>
                <span style={{ fontSize: 13.5, color: color.inkSubtle }}>{reach(secret)}</span>
                <span style={{ fontSize: 13, color: color.inkSubtle }}>
                  set {proseDay(secret.updatedAt)} by {actorLabel(secret.updatedBy)}
                </span>
                {admin ? (
                  <RowAction danger onClick={() => void remove(secret)}>
                    Remove
                  </RowAction>
                ) : null}
              </Row>
            ))}
          </Table>
        ) : null}
      </div>
      {removed && (
        <Note tone="teal">{removed} is removed. Every member’s machine drops it at its next check.</Note>
      )}
      {secrets?.available ? (
        <Callout title="What this page can do">
          A value is typed in Airlock and sealed there; this page never receives it and cannot set one. A workspace’s own variable of the same
          name wins over an organization secret. Limiting a secret to some teams is enforced by the account area, not by the key — every
          machine holding the key could open it if it were handed over.
        </Callout>
      ) : null}
    </PageBody>
  );
}
